import { LoaderFunction, redirect } from "react-router-dom";

import { orgStorage } from "@/shared/auth";
import { DatasetRecord } from "@/shared/domain/datasets";
import {
  SortColumn,
  SortDirection,
  isSortColumn,
  isSortDirection,
} from "@/shared/domain/files/FileService";
import { QueryService, QueryTarget } from "@/shared/domain/query";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation/Goto";
import { ErrorMonitoringService } from "@/shared/services";

/**
 * Loads the datasets that match the given name and redirects
 * to either the single dataset match if there is only one match,
 * or the dataset search results page otherwise.
 */
export const loadDatasetByName: LoaderFunction = async ({
  params,
}): Promise<Response> => {
  try {
    const { datasetName } = params;
    const httpClient = await makeAuthenticatedHttpClient();

    const queryService = new QueryService(httpClient);
    const orgRecord = orgStorage.getCurrentOrg();

    // Redirect user to select orgs if org not found
    if (!orgRecord) {
      return redirect(`${Goto.route.SignIn}?chooseorg=true`);
    }
    const query = await queryService.submitRoboql(
      {
        target: QueryTarget.Datasets,
        query: `name = "${datasetName}"`,
      },
      { resourceOwnerId: orgRecord.org_id },
    );

    const resultCount = await queryService.getQueryResultCount(query.query_id);
    if (resultCount !== 1) {
      // If multiple datasets match the given name, go to the search results page.
      return redirect(`${Goto.route.Search}?queryId=${query.query_id}`);
    }

    const results = await queryService.getQueryResults(query.query_id);
    const datasetId = (results.items[0] as DatasetRecord).dataset_id;
    // If a single dataset resolves, go directly there.
    return redirect(`${Goto.route.Datasets}/${datasetId}`);
  } catch (error) {
    // Go to the full datasets list when an error occurs.
    ErrorMonitoringService.captureError(error);
    return redirect(Goto.route.Datasets);
  }
};

export const loadSortParamsFromUrl: LoaderFunction = ({ request }) => {
  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.search);

  const sortColumnParam = searchParams.get("sortColumn");
  const sortDirectionParam = searchParams.get("sortDirection");

  let sortColumn: SortColumn = "name";
  let sortDirection: SortDirection = "ASC";
  // This function must contain all type validation logic for sort params. Downstream consumers must be able to rely on the
  // types being correct.
  if (sortColumnParam !== null && isSortColumn(sortColumnParam)) {
    sortColumn = sortColumnParam;
  }

  if (sortDirectionParam !== null && isSortDirection(sortDirectionParam)) {
    sortDirection = sortDirectionParam;
  }

  return { sortColumn, sortDirection };
};
